import * as React from "react";
import { api, type ApiOrg, type ApiUser, type AttendeeUpload, type Registrant } from "../api.js";
import { Icon } from "../components/Icon.js";

type Props = {
  user: ApiUser;
  org: ApiOrg | null;
  instance: string;
};

type EventLite = { id: string; name: string; startsAt: string | null };

/** Per-event count of attendees we know are somebody other than the
 *  buyer but still hold no contact record for. Derived from the
 *  registrants payload the page already loads rather than a second
 *  endpoint: it's the same signal the drawer uses to explain a row's
 *  blanks, so the two can't disagree. */
type UnresolvedCounts = Record<string, number>;

function countUnresolved(registrants: Registrant[]): UnresolvedCounts {
  const out: UnresolvedCounts = {};
  for (const r of registrants) {
    // purchaserName is only set when the attendee is a third party, and
    // attendeeContactKnown is false only when we couldn't source their
    // own record — so together they are exactly "still unresolved".
    if (r.purchaserName && !r.attendeeContactKnown) out[r.eventId] = (out[r.eventId] ?? 0) + 1;
  }
  return out;
}

type RowState = {
  uploading: boolean;
  error: string | null;
  warnings: string[];
  notice?: string | null;
};

function formatUploadedAt(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function AttendeeOverridesScreen({ user, org, instance }: Props) {
  const [events, setEvents] = React.useState<EventLite[] | null>(null);
  const [uploads, setUploads] = React.useState<AttendeeUpload[]>([]);
  const [unresolved, setUnresolved] = React.useState<UnresolvedCounts>({});
  const [error, setError] = React.useState<string | null>(null);
  const [rowState, setRowState] = React.useState<Record<string, RowState>>({});
  const [resyncing, setResyncing] = React.useState(false);
  const [resyncNote, setResyncNote] = React.useState<string | null>(null);
  const fileInputs = React.useRef<Record<string, HTMLInputElement | null>>({});

  const loadAll = React.useCallback(async () => {
    // Reuse the registrants payload's events list — we already fetch it
    // for the dashboard and it's cheap by comparison to hitting a new
    // events endpoint we don't have server-side.
    const [regs, ups] = await Promise.all([api.registrants(), api.attendees.list()]);
    setEvents(regs.events.map((e) => ({ id: e.id, name: e.name, startsAt: e.startsAt })));
    setUploads(ups.uploads);
    setUnresolved(countUnresolved(regs.registrants));
  }, []);

  React.useEffect(() => {
    let alive = true;
    loadAll().catch((e) => { if (alive) setError(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, [loadAll]);

  if (error === "unauthenticated") {
    window.location.href = "/auth/login";
    return null;
  }
  if (error) return <div className="card" style={{ margin: 24, padding: 20 }}><b>Error:</b> {error}</div>;
  if (!events) return <div className="card" style={{ margin: 24, padding: 20 }}>Loading…</div>;

  const uploadByEvent = new Map(uploads.map((u) => [u.eventId, u]));

  async function handleFile(eventId: string, file: File) {
    setRowState((s) => ({ ...s, [eventId]: { uploading: true, error: null, warnings: [] } }));
    try {
      const { upload, errors } = await api.attendees.upload(eventId, file);
      setUploads((prev) => {
        const others = prev.filter((u) => u.eventId !== eventId);
        return [...others, upload].sort((a, b) => a.eventId.localeCompare(b.eventId));
      });
      setRowState((s) => ({ ...s, [eventId]: { uploading: false, error: null, warnings: errors } }));
      // The upload changes who counts as unresolved, and the registrants
      // fetch it triggers is also what runs the lookup pass — so re-read
      // rather than leaving a stale count on screen.
      await loadAll();
    } catch (e) {
      setRowState((s) => ({ ...s, [eventId]: { uploading: false, error: String((e as Error).message ?? e), warnings: [] } }));
    } finally {
      const input = fileInputs.current[eventId];
      if (input) input.value = "";
    }
  }

  // Pulls every pack activation still inside TidyHQ's 30-day retention
  // and re-applies it, which is what attaches a real contact record to
  // an attendee who registered before the webhook existed.
  async function handleResync() {
    setResyncing(true);
    setResyncNote(null);
    try {
      const { replay, uploads: fresh } = await api.attendees.resync();
      setUploads(fresh);
      const bits = [
        `Re-applied ${replay.ticketsUpserted} ticket${replay.ticketsUpserted === 1 ? "" : "s"} from ${replay.applied} pack activation${replay.applied === 1 ? "" : "s"}.`,
      ];
      const stillCsv = fresh.reduce((n, u) => n + u.csvRowCount, 0);
      if (stillCsv > 0) {
        bits.push(`${stillCsv} ticket${stillCsv === 1 ? "" : "s"} still rely on an uploaded CSV — TidyHQ only retains 30 days.`);
      }
      if (replay.hitLimit) {
        bits.push("TidyHQ returned a full page — older activations inside the 30-day window may not have been seen.");
      }
      setResyncNote(bits.join(" "));
      // A re-sync also clears the "we looked and found nobody" memo, so
      // the next registrants read retries those lookups.
      await loadAll();
    } catch (e) {
      setResyncNote(`Re-sync failed: ${String((e as Error).message ?? e)}`);
    } finally {
      setResyncing(false);
    }
  }

  async function handleRemove(eventId: string) {
    if (!confirm(
      "Remove the uploaded attendee CSV for this event?\n\n" +
      "Attendees established from TidyHQ's webhook are kept — they aren't part of this upload.",
    )) return;
    setRowState((s) => ({ ...s, [eventId]: { uploading: true, error: null, warnings: [] } }));
    try {
      await api.attendees.remove(eventId);
      // Webhook rows survive the delete, so re-read rather than assuming
      // the event has no attendee data left.
      await loadAll();
      setRowState((s) => ({ ...s, [eventId]: { uploading: false, error: null, warnings: [] } }));
    } catch (e) {
      setRowState((s) => ({ ...s, [eventId]: { uploading: false, error: String((e as Error).message ?? e), warnings: [] } }));
    }
  }

  const userInitials = (() => {
    const name = user.displayName ?? user.email ?? "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return "?";
  })();
  const orgInitial = (org?.name ?? "?")[0]!.toUpperCase();

  return (
    <div className="dash-root">
      <header className="topbar">
        <div className="tb-l">
          <div className="brand-mark">
            <span className="dot" />Tidy<b>View</b>
          </div>
          <div className="org-pill" title={org?.name ?? ""}>
            <div className="avatar" style={{ background: "#1a8fbf", width: 22, height: 22, fontSize: 10 }}>{orgInitial}</div>
            <span>{org?.name ?? "—"}</span>
          </div>
        </div>
        <div className="tb-r">
          <a href="#/webhooks" className="btn btn-ghost btn-sm">
            <Icon name="sparkles" size={13} /> Webhooks
          </a>
          <a href="#/" className="btn btn-secondary btn-sm">
            <Icon name="chevLeft" size={13} /> Back to dashboard
          </a>
          <div className="avatar" style={{ background: "#1a8fbf" }} title={user.displayName ?? ""}>{userInitials}</div>
        </div>
      </header>

      <main style={{ padding: "24px", maxWidth: 960, margin: "0 auto" }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22, letterSpacing: "-0.01em", color: "var(--ink-900)" }}>Attendee overrides</h1>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 13.5, maxWidth: 640 }}>
            Attendee identity comes from TidyHQ's pack webhook wherever it
            can: those rows carry the attendee's contact id, so their real
            record — email, membership, custom fields — resolves properly.
            Re-syncing replays everything inside TidyHQ's 30-day retention,
            including registrations from before the webhook existed. Uploading
            the CSV export (Event → Details → Export) covers what falls outside
            that window; it has no contact id column, so we look each attendee
            up by email and name instead — accepting a match only when the
            name agrees, and leaving the row blank rather than risk showing
            somebody else's answers.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn btn-secondary btn-sm" disabled={resyncing} onClick={() => void handleResync()}>
              <Icon name="sparkles" size={13} /> {resyncing ? "Re-syncing…" : "Re-sync from TidyHQ"}
            </button>
            {resyncNote && (
              <span className="muted" style={{ fontSize: 12.5 }}>{resyncNote}</span>
            )}
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <table className="ao-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--ink-50)", textAlign: "left", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-600)" }}>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Event</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Upload</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={3} style={{ padding: 20, color: "var(--ink-500)" }}>No events available on this account yet.</td></tr>
              )}
              {events.map((e) => {
                const upload = uploadByEvent.get(e.id);
                const unresolvedHere = unresolved[e.id] ?? 0;
                const state = rowState[e.id] ?? { uploading: false, error: null, warnings: [] };
                return (
                  <tr key={e.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px 16px", verticalAlign: "top" }}>
                      <div style={{ fontWeight: 600, color: "var(--ink-900)" }}>{e.name}</div>
                      {e.startsAt && (
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          Starts {new Date(e.startsAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", verticalAlign: "top", fontSize: 13 }}>
                      {upload ? (
                        <div>
                          <div>
                            <b>{upload.webhookRowCount}</b> from webhook
                            {" · "}
                            <b>{upload.csvRowCount}</b> from CSV
                            {upload.resolvedRowCount > 0 && (
                              <>
                                {" · "}
                                <b>{upload.resolvedRowCount}</b>{" "}
                                <span title="Uploaded rows whose attendee we matched to a TidyHQ contact by email and name.">
                                  matched to a contact
                                </span>
                              </>
                            )}
                          </div>
                          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                            {upload.csvRowCount === 0
                              ? "Fully covered by TidyHQ — no upload needed."
                              : "CSV rows cover tickets the webhook can't reach."}
                            {" "}
                            {formatUploadedAt(upload.uploadedAt)}
                          </div>
                        </div>
                      ) : (
                        <span className="muted">No attendee data yet</span>
                      )}
                      {unresolvedHere > 0 && (
                        <div
                          style={{ color: "var(--amber-500)", fontSize: 12.5, marginTop: 6 }}
                          title={
                            "We know these attendees are somebody other than the person who paid, " +
                            "but have no TidyHQ contact for them — so their email, membership and " +
                            "custom answers show as blank rather than the buyer's."
                          }
                        >
                          {unresolvedHere} attendee{unresolvedHere === 1 ? "" : "s"} unresolved
                        </div>
                      )}
                      {state.notice && (
                        <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>{state.notice}</div>
                      )}
                      {state.error && (
                        <div style={{ color: "var(--red-500)", fontSize: 12.5, marginTop: 6, maxWidth: 420 }}>
                          <b>Upload failed:</b> {state.error}
                        </div>
                      )}
                      {state.warnings.length > 0 && (
                        <div style={{ color: "var(--amber-500)", fontSize: 12.5, marginTop: 6, maxWidth: 420 }}>
                          {state.warnings.map((w, i) => <div key={i}>{w}</div>)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", verticalAlign: "top", whiteSpace: "nowrap" }}>
                      <input
                        ref={(el) => { fileInputs.current[e.id] = el; }}
                        type="file"
                        accept=".csv,text/csv"
                        style={{ display: "none" }}
                        onChange={(ev) => {
                          const f = ev.target.files?.[0];
                          if (f) void handleFile(e.id, f);
                        }}
                      />
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={state.uploading}
                        onClick={() => fileInputs.current[e.id]?.click()}
                      >
                        <Icon name="upload" size={13} /> {upload ? "Replace" : "Upload CSV"}
                      </button>
                      {upload && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ marginLeft: 6 }}
                          disabled={state.uploading}
                          onClick={() => void handleRemove(e.id)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          Signed in to <b>{instance}</b>. Uploaded CSVs are scoped to your
          organisation and stay on this server — they aren't sent back to
          TidyHQ.
        </p>
      </main>
    </div>
  );
}
